import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './BlogPost.css';
import { 
    ArrowLeft, 
    Eye, 
    Heart, 
    Share2, 
    User, 
    Clock,
    Tag,
    Crown,
    Lock
} from 'lucide-react';
import { ApiError } from '../../../lib/apiClient';
import { appConfig } from '../../../config/appConfig';
import { blogService } from '../../../services';
import PremiumUnlockCard from '../../../platform/components/subscription/PremiumUnlockCard';

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [liked, setLiked] = useState(false);

    const fetchPost = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await blogService.getPublicPost(slug);
            setPost(data);
        } catch (err) {
            console.error('Error fetching post:', err);
            if (err instanceof ApiError && err.status === 401) {
                setError('members-only');
                return;
            }
            if (err instanceof ApiError && err.status === 403) {
                setError('premium-only');
                return;
            }
            setError('not-found');
        } finally {
            setIsLoading(false);
        }
    }, [slug]);

    useEffect(() => {
        fetchPost();
    }, [fetchPost]);

    const handleLike = async () => {
        if (liked || !post) return;
        
        try {
            const data = await blogService.likePost(post.postId);
            setPost(prev => ({ ...prev, likeCount: data.likeCount }));
            setLiked(true);
        } catch (err) {
            console.error('Error liking post:', err);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: post?.title,
                    text: post?.excerpt,
                    url
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            navigator.clipboard.writeText(url);
            alert('Link copied to clipboard!');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const getReadingTime = (content) => {
        if (!content) return '1 min';
        const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
        const minutes = Math.ceil(words / 200);
        return `${minutes} min read`;
    };

    if (isLoading) {
        return (
            <div className="blogpost-loading">
                <div className="blogpost-loading-spinner"></div>
                <p>Loading article...</p>
            </div>
        );
    }

    if (error === 'members-only') {
        return (
            <div className="blogpost-restricted">
                <Lock size={64} />
                <h2>Members Only Content</h2>
                <p>This article is available exclusively for registered members</p>
                <button onClick={() => navigate('/login')}>
                    Sign In to Read
                </button>
            </div>
        );
    }

    if (error === 'premium-only') {
        return (
            <div className="blogpost-restricted blogpost-premium">
                <Crown size={64} />
                <h2>Premium Content</h2>
                <p>This article is part of the Premium member library.</p>
                <PremiumUnlockCard
                    compact
                    title="Free vs Premium Access"
                    description="Free keeps key discovery tools open, while Premium unlocks this article and deeper member workflows."
                    highlights={[
                        'Free includes marketplace, venue discovery, and public blog/news.',
                        'Premium unlocks premium articles and advanced scoring value.',
                        'Premium includes deeper analytics and extra perks.'
                    ]}
                    ctaLabel="See Premium Plans"
                />
                <button className="secondary" onClick={() => navigate('/dashboard/blog')}>
                    Back to Blog
                </button>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="blogpost-error">
                <h2>Article Not Found</h2>
                <p>The article you're looking for doesn't exist or has been removed</p>
                <button onClick={() => navigate('/dashboard/blog')}>
                    Back to Blog
                </button>
            </div>
        );
    }

    return (
        <div className="blogpost-page">
            <div className="blogpost-navigation">
                <button className="blogpost-back" onClick={() => navigate('/dashboard/blog')}>
                    <ArrowLeft size={18} />
                    Back to Blog
                </button>
            </div>

            <article className="blogpost-article">
                {/* Header */}
                <header className="blogpost-header">
                    <div className="blogpost-meta-top">
                        <span className="blogpost-category">{post.category || 'News'}</span>
                        {post.visibility === 'Premium' && (
                            <span className="blogpost-badge premium"><Crown size={12} /> Premium</span>
                        )}
                        {post.visibility === 'MembersOnly' && (
                            <span className="blogpost-badge members"><Lock size={12} /> Members</span>
                        )}
                    </div>
                    
                    <h1 className="blogpost-title">{post.title}</h1>
                    
                    {post.excerpt && (
                        <p className="blogpost-excerpt">{post.excerpt}</p>
                    )}

                    <div className="blogpost-meta">
                        <div className="blogpost-author">
                            {post.authorProfileImage ? (
                                <img src={post.authorProfileImage} alt={post.authorName} />
                            ) : (
                                <div className="blogpost-author-placeholder">
                                    <User size={20} />
                                </div>
                            )}
                            <div>
                                <span className="blogpost-author-name">
                                    {post.authorDisplayName || post.authorName}
                                </span>
                                <span className="blogpost-author-date">
                                    {formatDate(post.publishedAt)}
                                </span>
                            </div>
                        </div>
                        
                        <div className="blogpost-stats">
                            <span><Clock size={14} /> {getReadingTime(post.content)}</span>
                            <span><Eye size={14} /> {post.viewCount} views</span>
                        </div>
                    </div>
                </header>

                {/* Featured Image */}
                {post.featuredImageUrl && (
                    <div className="blogpost-featured-image">
                        <img src={post.featuredImageUrl} alt={post.title} />
                    </div>
                )}

                {/* Content */}
                <div 
                    className="blogpost-content"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {post.tags && (
                    <div className="blogpost-tags">
                        <Tag size={16} />
                        {post.tags.split(',').map((tag, index) => (
                            <span key={index} className="blogpost-tag">
                                {tag.trim()}
                            </span>
                        ))}
                    </div>
                )}

                {/* Actions */}
                <div className="blogpost-actions">
                    <button 
                        className={`blogpost-action-btn ${liked ? 'liked' : ''}`}
                        onClick={handleLike}
                        disabled={liked}
                    >
                        <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
                        <span>{post.likeCount} Likes</span>
                    </button>
                    
                    <button className="blogpost-action-btn" onClick={handleShare}>
                        <Share2 size={20} />
                        <span>Share</span>
                    </button>
                </div>

                {/* Author Bio */}
                <div className="blogpost-author-bio">
                    {post.authorProfileImage ? (
                        <img src={post.authorProfileImage} alt={post.authorName} />
                    ) : (
                        <div className="blogpost-author-bio-placeholder">
                            <User size={32} />
                        </div>
                    )}
                    <div>
                        <h4>Written by {post.authorDisplayName || post.authorName}</h4>
                        <p>Contributor at {appConfig.appDisplayName}</p>
                    </div>
                </div>
            </article>
        </div>
    );
};

export default BlogPost;
